import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {ModalDirective} from 'ngx-bootstrap/modal';
import {TranslocoService} from '@jsverse/transloco';
import {Subscription} from 'rxjs';
import * as wjcCore from '@mescius/wijmo';
import {asOrientDate, isOrientDate, DataService} from '@cosbeni/database';
import {CalendarOptions, EventClickArg, DatesSetArg, EventInput} from '@fullcalendar/core';
import jaLocale from '@fullcalendar/core/locales/ja';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {ActivePromotionConfig} from '../config/active-promotion.config';

/**
 * 買取屋 カレンダー (旧 kaitoriya.app /user `cj-user-calendar` の移植)。
 *
 * 旧画面は DayPilot Pro (2021.1, Angular2 世代のライセンス版) 依存だったため、
 * Angular19 では OSS の FullCalendar v6 で再実装する。データ挙動は踏襲:
 *  - 月/週/日表示。表示範囲変更 (datesSet) で AP_Order_HEAD の startDate が
 *    可視範囲に入る伝票を取得しイベント化する。
 *  - イベントクリックで伝票ヘッダ + 明細 (AP_Order_Line) を読み取り専用モーダル表示。
 *  - モーダルの「編集」で買取伝票画面へ遷移する。
 *  - 買取区分 (AP_Master colorId) でイベント色を付ける。
 */
@Component({
    standalone: false,
    selector: 'app-kaitoriya-calendar',
    templateUrl: 'calendar-management.component.html',
})
export class KaitoriyaCalendarManagementComponent implements OnInit, OnDestroy {
    @ViewChild('detailModal') detailModal: ModalDirective;

    /** colorId(1-11) → 表示色。Google カレンダー相当の淡色パレット。 */
    private readonly colorPalette: {[id: number]: string} = {
        1: '#7986cb', 2: '#33b679', 3: '#8e24aa', 4: '#e67c73', 5: '#f6bf26', 6: '#f4511e',
        7: '#039be5', 8: '#616161', 9: '#3f51b5', 10: '#0b8043', 11: '#d50000',
    };
    /** 買取区分(status) → colorId のルックアップ (AP_Master から構築)。 */
    private statusColorId: {[status: string]: number} = {};

    /** 選択中の伝票ヘッダ (モーダル表示用)。 */
    selectedOrder: any = null;
    selectedAddress = '';
    /** 選択中伝票の明細 (読み取り専用)。 */
    orderLine: wjcCore.CollectionView = new wjcCore.CollectionView([]);

    calendarOptions: CalendarOptions = {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        locales: [jaLocale],
        locale: 'ja',
        headerToolbar: {
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
        },
        height: 'auto',
        nowIndicator: true,
        slotMinTime: '08:00:00',
        events: [],
        datesSet: this.onDatesSet.bind(this),
        eventClick: this.onEventClick.bind(this),
    };

    private langSub: Subscription;

    constructor(private dataService: DataService, private router: Router, private transloco: TranslocoService) {}

    ngOnInit(): void {
        // 表示言語に合わせて FullCalendar のロケール (ボタン・曜日表記) を切り替える。
        this.langSub = this.transloco.langChanges$.subscribe(lang => {
            const locale = lang === 'en' ? 'en' : 'ja';
            if (this.calendarOptions.locale !== locale) {
                this.calendarOptions = Object.assign({}, this.calendarOptions, {locale: locale});
            }
        });
        // 買取区分マスタから status→colorId を構築 (色付けに使用)。失敗しても無色で継続。
        this.dataService.query({
            sql: 'SELECT FROM AP_Master WHERE category =\'買取区分\' order by sort',
            count: 'SELECT FROM AP_Master WHERE category =\'買取区分\' order by sort',
            fetchPlan: '*:1',
        }, ActivePromotionConfig.SERVER_URL).subscribe((data: any) => {
            const rows = wjcCore.isArray(data.results) ? data.results : [];
            rows.forEach((row: any) => {
                if (row && !wjcCore.isNullOrWhiteSpace(row.value) && row.colorId != null) {
                    this.statusColorId[row.value] = Number(row.colorId);
                }
            });
        });
    }

    /** 可視範囲が変わるたびに AP_Order_HEAD を取得しイベントを差し替える。 */
    private onDatesSet(arg: DatesSetArg): void {
        const start = wjcCore.Globalize.formatDate(arg.start, 'yyyy-MM-dd HH:mm:ss');
        const end = wjcCore.Globalize.formatDate(arg.end, 'yyyy-MM-dd HH:mm:ss');
        const sql = 'SELECT FROM AP_Order_HEAD WHERE (startDate >= \'' + start + '\' AND startDate < \'' + end + '\')';
        this.dataService.query({sql: sql, count: sql, fetchPlan: '*:1'}, ActivePromotionConfig.SERVER_URL)
            .subscribe((data: any) => {
                const rows = wjcCore.isArray(data.results) ? data.results : [];
                const events: EventInput[] = [];
                rows.forEach((row: any) => {
                    const start2 = this.toDate(row.startDate);
                    if (start2 == null) {
                        return;
                    }
                    const end2 = this.toDate(row.endDate);
                    const address = this.buildAddress(row);
                    const title = (row.name || '') + (address ? ' (' + address + ')' : '');
                    const event: EventInput = {
                        id: row.uuid,
                        title: title,
                        start: start2,
                        extendedProps: {order: row},
                    };
                    if (end2 != null) {
                        event.end = end2;
                    }
                    const colorId = this.statusColorId[row.status];
                    if (colorId != null && this.colorPalette[colorId]) {
                        event.color = this.colorPalette[colorId];
                    }
                    events.push(event);
                });
                // options を作り直して再描画させる (events だけの差し替えでは反映されない)。
                this.calendarOptions = Object.assign({}, this.calendarOptions, {events: events});
            });
    }

    /** イベントクリックで伝票ヘッダ + 明細を読み取り専用モーダル表示。 */
    private onEventClick(arg: EventClickArg): void {
        const order = arg.event.extendedProps ? arg.event.extendedProps['order'] : null;
        if (order == null) {
            return;
        }
        this.selectedOrder = order;
        this.selectedAddress = this.buildAddress(order);
        this.orderLine = new wjcCore.CollectionView([]);
        const rid = order['@rid'];
        if (!wjcCore.isNullOrWhiteSpace(rid)) {
            const sql = 'SELECT FROM AP_Order_Line WHERE header = ' + rid + ' ORDER BY sort';
            this.dataService.query({sql: sql, count: sql, fetchPlan: '*:1'}, ActivePromotionConfig.SERVER_URL)
                .subscribe((data: any) => {
                    this.orderLine = new wjcCore.CollectionView(wjcCore.isArray(data.results) ? data.results : []);
                });
        }
        if (this.detailModal) {
            this.detailModal.show();
        }
    }

    /** 買取伝票画面へ遷移して編集する (旧 ngrx deep-link は当アプリ未対応のため単純遷移)。 */
    editOrder(): void {
        if (this.detailModal) {
            this.detailModal.hide();
        }
        this.router.navigate(['/kaitoriya/orders']);
    }

    /** OrientDB 日付値を JS Date に変換 (不正/空は null)。 */
    private toDate(value: any): Date {
        if (value == null || value === '') {
            return null;
        }
        if (isOrientDate(value)) {
            value = asOrientDate(value);
        }
        if (wjcCore.isDate(value)) {
            return value;
        }
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }

    /** 都道府県+市区町村+町名+建物を連結。 */
    private buildAddress(item: any): string {
        let address = '';
        if (item == null) {
            return address;
        }
        if (!wjcCore.isNullOrWhiteSpace(item.state)) {
            address = address + item.state;
        }
        if (!wjcCore.isNullOrWhiteSpace(item.city)) {
            address = address + item.city;
        }
        if (!wjcCore.isNullOrWhiteSpace(item.street)) {
            address = address + item.street;
        }
        if (!wjcCore.isNullOrWhiteSpace(item.building)) {
            address = address + item.building;
        }
        return address;
    }

    ngOnDestroy(): void {
        if (this.langSub) {
            this.langSub.unsubscribe();
        }
    }

    /** モーダル表示用の日時文字列。 */
    formatDate(value: any, format: string): string {
        const d = this.toDate(value);
        return d == null ? '' : wjcCore.Globalize.formatDate(d, format);
    }
}
